import { Box, Theme, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { observer } from "mobx-react-lite";
import { StepCard } from "./StepCard";
import { useContext, useEffect, useMemo } from "react";
import ReviewContext from "context/ReviewContext";
import {
	CreateReviewData,
	CreateReviewTransition,
	ENegativeStrategy,
	ERatingSystem,
} from "state/ReviewState";
import { FETCH_STATUS } from "descriptions";
import RatingThumbs from "components/RatingThumbs/RatingThumbs";
import RatingStars from "components/RatingStars/RatingStars";
import RatingNps from "components/RatingNps/RatingNps";
import RatingCsat from "components/RatingCsat/RatingCsat";
import LanguageContext from "context/LanguageContext";

const useStyles = makeStyles()((theme: Theme) => ({
	address: {
		marginTop: theme.spacing(0.5),
		marginBottom: theme.spacing(5),
		color: theme.palette.text.secondary,
	},
	textCenter: {
		textAlign: "center",
	},
}));

interface IProps {
	hash: string;
}

export const SelectRatingStep = observer(({ hash }: IProps) => {
	const { classes, cx } = useStyles();
	const { getTranslate } = useContext(LanguageContext);
	const {
		evaluation,
		setEvaluation,
		setStep,
		location,
		fetchCreateReview,
		fetchCreateReviewService,
		fetchCreateTransition,
		fetchCreateTransitionService,
		ratingSystem,
		ratingValue,
		setRatingValue,
		negativeStrategy,
		redirectLink,
	} = useContext(ReviewContext);

	const transitionSubmit = () => {
		const data: CreateReviewTransition = {
			hash,
			session_id: localStorage.getItem(window.location.pathname) || "",
			uuid: localStorage.getItem("uuid") || "",
		};
		fetchCreateTransition(data);
	};

	const getRatingValue = useMemo(() => {
		switch (ratingSystem) {
			case ERatingSystem.THUMBS:
				return evaluation === "like" ? 1 : 0;
			case ERatingSystem.STARS:
			case ERatingSystem.CSAT:
			case ERatingSystem.NPS:
				return ratingValue;
			default:
				return 1;
		}
	}, [ratingSystem, evaluation, ratingValue]);

	const handleSubmit = () => {
		const data: CreateReviewData = {
			session_id: localStorage.getItem(window.location.pathname) || "",
			hash,
			rating_system: ratingSystem ? ratingSystem : ERatingSystem.THUMBS,
			rating_value: getRatingValue ?? 0,
			uuid: localStorage.getItem("uuid") || "",
		};
		fetchCreateReview(data).then(({ status }) => {
			const geoservices = Object.keys(location.urls);
			if (status === FETCH_STATUS.DONE) {
				switch (negativeStrategy) {
					case ENegativeStrategy.SURVEY:
						setStep(evaluation === "dislike" || !geoservices.length ? 1 : 3);
						break;
					case ENegativeStrategy.REDIRECT:
						if (evaluation === "dislike") {
							if (redirectLink) {
								setTimeout(() => window.open(redirectLink, "_self"));
							} else {
								setStep(4);
							}
						} else {
							setStep(3);
						}
						break;
					default:
						break;
				}
			}
		});
	};

	const getRatingType = useMemo(() => {
		switch (ratingSystem) {
			case ERatingSystem.THUMBS:
				return <RatingThumbs evaluation={evaluation} setEvaluation={setEvaluation} />;
			case ERatingSystem.STARS:
				return <RatingStars ratingValue={ratingValue} setRatingValue={setRatingValue} />;
			case ERatingSystem.CSAT:
				return <RatingCsat ratingValue={ratingValue} setRatingValue={setRatingValue} />;
			case ERatingSystem.NPS:
				return <RatingNps ratingValue={ratingValue} setRatingValue={setRatingValue} />;
			default:
				return <Box p={2}>{getTranslate("errorCard.title")}</Box>;
		}
	}, [ratingSystem, evaluation, ratingValue]);

	const getDisabledSubmit = useMemo(() => {
		switch (ratingSystem) {
			case ERatingSystem.THUMBS:
				return evaluation === null;
			case ERatingSystem.STARS:
			case ERatingSystem.CSAT:
			case ERatingSystem.NPS:
				return !ratingValue;
			default:
				return true;
		}
	}, [ratingValue, ratingSystem, evaluation]);

	useEffect(() => {
		if (fetchCreateTransitionService.loading === FETCH_STATUS.WAIT) {
			transitionSubmit();
		}
	}, [fetchCreateTransitionService.loading]);

	return (
		<StepCard
			title="reviewPage.selectRatingStep.title"
			subTitle="reviewPage.selectRatingStep.subTitle"
			onSubmit={handleSubmit}
			onCancel={() => setStep(4)}
			submitTitle="reviewPage.selectRatingStep.submit"
			cancelTitle="reviewPage.selectRatingStep.cancel"
			isLoading={fetchCreateReviewService.loading === FETCH_STATUS.PENDING}
			disabledSubmit={getDisabledSubmit}
		>
			<Typography variant="h4">{location?.name}</Typography>
			<Typography className={cx(classes.address, classes.textCenter)}>
				{location?.address}
			</Typography>
			{getRatingType}
		</StepCard>
	);
});
